import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { eur, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Wallet, Lock, Receipt, Settings2, TrendingUp, Download, Mail, MailX, MailCheck, Trash2, ListChecks, Scissors, Package, Clock } from "lucide-react";

const COMP_TYPES = [
  { v: "none", l: "Nessuno" }, { v: "hourly", l: "Oraria" }, { v: "commission", l: "Percentuale" },
  { v: "hourly_commission", l: "Oraria + %" }, { v: "fixed", l: "Fisso" },
  { v: "fixed_commission", l: "Fisso + %" },
];
const FREQ = [{ v: "settimanale", l: "Settimanale" }, { v: "quindicinale", l: "Quindicinale" }, { v: "mensile", l: "Mensile" }];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const now = new Date();
const mStart = new Date(now.getFullYear(), now.getMonth(), 1);

export default function Retribuzioni() {
  const [from, setFrom] = useState(iso(mStart));
  const [to, setTo] = useState(iso(now));
  const [rows, setRows] = useState(null);
  const [history, setHistory] = useState([]);
  const [pay, setPay] = useState(null); // row being paid
  const [adjustment, setAdjustment] = useState(0);
  const [bonus, setBonus] = useState(0);
  const [deductions, setDeductions] = useState(0);
  const [advances, setAdvances] = useState(0);
  const [note, setNote] = useState("");
  const [cfg, setCfg] = useState(null); // compensation config row
  const [savingCfg, setSavingCfg] = useState(false);
  const [detail, setDetail] = useState(null); // riepilogo lavori collaboratore
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data || [])).catch(() => setServices([])); }, []);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const [s, h] = await Promise.all([
        api.get(`/payroll/summary?from=${from}&to=${to}`),
        api.get("/payroll/payments"),
      ]);
      setRows(s.data.rows); setHistory(h.data);
    } catch (e) { toast.error("Errore caricamento retribuzioni"); setRows([]); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const openPay = (r) => { setPay(r); setAdjustment(0); setBonus(0); setDeductions(0); setAdvances(0); setNote(""); };
  const [paying, setPaying] = useState(false);
  const confirmPay = async () => {
    setPaying(true);
    try {
      await api.post("/payroll/payments", {
        operator_id: pay.operator_id, operator_name: pay.operator_name, from, to,
        hours: pay.hours, components: pay, total_calculated: pay.total_gross,
        adjustment: Number(adjustment) || 0, manual_bonus: Number(bonus) || 0,
        deductions: Number(deductions) || 0,
        advances: Number(advances) || 0, note,
      });
      toast.success("Pagamento registrato · cedolino PDF generato e inviato via email");
      setPay(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setPaying(false); }
  };
  const netToPay = (pay?.total_gross || 0) + (Number(adjustment) || 0) + (Number(bonus) || 0) - (Number(deductions) || 0) - (Number(advances) || 0);

  const downloadPdf = async (p) => {
    try {
      const res = await api.get(`/payroll/payments/${p.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("PDF non disponibile"); }
  };

  const openCfg = (r) => setCfg({
    operator_id: r.operator_id, operator_name: r.operator_name,
    type: r.type || "commission", hourly_rate: r.hourly_rate || 0, fixed_salary: r.fixed || 0,
    service_pct: r.service_pct || 0, product_pct: r.product_pct || 0,
    revenue_target: r.revenue_target || 0, frequency: r.frequency || "mensile",
    bonus: r.bonus || 0, bonus_note: r.bonus_note || "",
    tiers: (r.tiers && r.tiers.length) ? r.tiers.map((t) => ({ over: t.over, pct: t.pct })) : [],
    excluded_service_ids: r.excluded_service_ids || [],
  });
  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await api.put(`/payroll/compensation/${cfg.operator_id}`, {
        type: cfg.type, hourly_rate: Number(cfg.hourly_rate) || 0, fixed_salary: Number(cfg.fixed_salary) || 0,
        service_pct: Number(cfg.service_pct) || 0, product_pct: Number(cfg.product_pct) || 0,
        revenue_target: Number(cfg.revenue_target) || 0, frequency: cfg.frequency,
        bonus: Number(cfg.bonus) || 0, bonus_note: cfg.bonus_note || "",
        tiers: (cfg.tiers || []).filter((t) => t.over !== "" && t.over != null).map((t) => ({ over: Number(t.over) || 0, pct: Number(t.pct) || 0 })),
        excluded_service_ids: cfg.excluded_service_ids || [],
      });
      toast.success("Compenso aggiornato"); setCfg(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSavingCfg(false); }
  };
  const costoPersonale = (rows || []).reduce((s, r) => s + (r.total_gross || 0), 0);

  // Riepilogo lavori: dettaglio per singolo servizio/prodotto nel periodo selezionato.
  // Riusa gli stessi calcoli del backend: i totali coincidono con la riga Retribuzioni.
  const openDetail = async (r) => {
    setDetailOpen(true); setDetail(null); setDetailLoading(true);
    try {
      const { data } = await api.get(`/payroll/detail?operator_id=${r.operator_id}&from=${from}&to=${to}`);
      setDetail(data);
    } catch (e) {
      toast.error("Errore caricamento riepilogo lavori");
      setDetailOpen(false);
    } finally { setDetailLoading(false); }
  };

  // Selettori rapidi di periodo (per vedere anche il guadagno giornaliero)
  const setToday = () => { const t = iso(new Date()); setFrom(t); setTo(t); };
  const setThisWeek = () => {
    const d = new Date(); const dow = (d.getDay() + 6) % 7; // 0 = lunedì
    const mon = new Date(d); mon.setDate(d.getDate() - dow);
    setFrom(iso(mon)); setTo(iso(new Date()));
  };
  const setThisMonth = () => { setFrom(iso(new Date(now.getFullYear(), new Date().getMonth(), 1))); setTo(iso(new Date())); };
  const isToday = from === to && from === iso(new Date());

  return (
    <div>
      <PageHeader title="Retribuzioni" subtitle="Calcolo paga indicativo (lordo) e storico pagamenti — dati riservati" />

      <div className="rounded-lg border border-border bg-accent/40 px-4 py-2.5 mb-5 text-sm flex items-center gap-2">
        <Lock className="h-4 w-4 text-[hsl(var(--hi-gold))]" />
        Sezione riservata all'Admin. Il calcolo è <strong>indicativo interno</strong> e non sostituisce buste paga o adempimenti fiscali.
      </div>

      <Card className="p-3 mb-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Periodo rapido</Label>
            <div className="flex gap-1">
              <Button type="button" size="sm" variant="outline" onClick={setToday} data-testid="pay-quick-today">Oggi</Button>
              <Button type="button" size="sm" variant="outline" onClick={setThisWeek} data-testid="pay-quick-week">Settimana</Button>
              <Button type="button" size="sm" variant="outline" onClick={setThisMonth} data-testid="pay-quick-month">Mese</Button>
            </div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Dal</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[150px]" data-testid="pay-from" /></div>
          <div className="space-y-1"><Label className="text-xs">Al</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[150px]" data-testid="pay-to" /></div>
          <div className="ml-auto text-right">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{isToday ? "Costo del personale (oggi)" : "Costo del personale (lordo)"}</div>
            <div className="font-heading text-2xl font-semibold text-[hsl(var(--hi-gold))]" data-testid="payroll-cost">{eur(costoPersonale)}</div>
          </div>
        </div>
      </Card>

      <Card className="p-0 mb-6">
        <div className="p-3 font-heading text-lg font-semibold">Riepilogo periodo</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Collaboratore</TableHead><TableHead>Ore</TableHead><TableHead>Base</TableHead>
            <TableHead>Comm. servizi</TableHead><TableHead>Comm. prodotti</TableHead>
            <TableHead>Obiettivo</TableHead>
            <TableHead>Media/giorno</TableHead>
            <TableHead>Totale lordo</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows === null && Array.from({ length: 4 }).map((_, i) => <TableRow key={i}><TableCell colSpan={9}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)}
            {rows && rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8" data-testid="pay-rows-empty">Nessun collaboratore configurato.</TableCell></TableRow>}
            {rows && rows.map((r) => (
              <TableRow key={r.operator_id} data-testid={`pay-row-${r.operator_id}`}>
                <TableCell className="font-medium">{r.operator_name}<div className="text-[10px] text-muted-foreground capitalize">{r.job_title || r.role}</div></TableCell>
                <TableCell>{r.hours}h</TableCell>
                <TableCell>{eur(r.base_hourly + r.fixed)}</TableCell>
                <TableCell>{eur(r.commission_service)}<span className="text-[10px] text-muted-foreground"> ({r.service_pct}%)</span></TableCell>
                <TableCell>{eur(r.commission_product)}<span className="text-[10px] text-muted-foreground"> ({r.product_pct}%)</span></TableCell>
                <TableCell className="min-w-[130px]">
                  {r.revenue_target > 0 ? (
                    <div data-testid={`pay-target-${r.operator_id}`}>
                      <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-0.5"><span>{eur(r.revenue_total)}</span><span>{r.target_progress}%</span></div>
                      <Progress value={r.target_progress || 0} className="h-1.5" />
                      <div className="text-[10px] text-muted-foreground mt-0.5">obiettivo {eur(r.revenue_target)}</div>
                    </div>
                  ) : <span className="text-[10px] text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="tabular text-sm text-muted-foreground" data-testid={`pay-avgday-${r.operator_id}`}>{eur(r.avg_per_day || 0)}</TableCell>
                <TableCell className="font-semibold text-[hsl(var(--hi-gold))]">{eur(r.total_gross)}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <div className="flex items-center gap-1 justify-end">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openCfg(r)} data-testid={`pay-config-${r.operator_id}`} title="Configura compenso"><Settings2 className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openDetail(r)} data-testid={`pay-detail-${r.operator_id}`} title="Riepilogo lavori del periodo"><ListChecks className="h-3.5 w-3.5 mr-1" /> Riepilogo lavori</Button>
                    <Button size="sm" variant="outline" onClick={() => openPay(r)} data-testid={`pay-btn-${r.operator_id}`}><Wallet className="h-3.5 w-3.5 mr-1" /> Registra</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <h2 className="font-heading text-xl font-semibold mb-3 flex items-center gap-2"><Receipt className="h-5 w-5" /> Storico pagamenti</h2>
      {history.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="pay-history-empty">Nessun pagamento registrato.</Card>
      ) : (
        <div className="grid gap-2">
          {history.map((p) => {
            const em = p.email || null;
            const emailBadge = em ? (
              em.status === "sent" ? { icon: MailCheck, label: "Email inviata", cls: "text-[hsl(var(--hi-status-success))] bg-[hsl(var(--hi-status-success)/0.12)]" }
              : em.status === "simulated" ? { icon: Mail, label: "Email (sandbox)", cls: "text-[hsl(var(--hi-status-info))] bg-[hsl(var(--hi-status-info)/0.12)]" }
              : em.status === "skipped_no_email" ? { icon: MailX, label: "Nessuna email", cls: "text-muted-foreground bg-muted" }
              : em.status === "failed" ? { icon: MailX, label: "Email non inviata", cls: "text-destructive bg-destructive/10" }
              : null
            ) : null;
            const EmIcon = emailBadge?.icon;
            return (
            <Card key={p.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`pay-history-${p.id}`}>
              <div className="flex-1 min-w-[160px]">
                <div className="font-medium text-sm">{p.operator_name} {p.adjusts && <Badge variant="outline" className="text-[9px] ml-1">rettifica</Badge>}</div>
                <div className="text-xs text-muted-foreground">{p.from} → {p.to} · {p.hours}h · {p.method}{p.doc_id ? ` · ${p.doc_id}` : ""}{p.note ? ` · ${p.note}` : ""}</div>
              </div>
              <div className="text-right">
                <div className="font-semibold">{eur(p.total_paid)}</div>
                {p.manual_bonus ? <div className="text-[10px] text-muted-foreground">bonus {eur(p.manual_bonus)}</div> : null}
              </div>
              {emailBadge && (
                <Badge className={`text-[10px] gap-1 ${emailBadge.cls}`} title={em.error || emailBadge.label} data-testid={`pay-email-status-${p.id}`}>
                  {EmIcon && <EmIcon className="h-3 w-3" />} {emailBadge.label}
                </Badge>
              )}
              <Badge className="text-[10px] bg-[hsl(var(--hi-status-success)/0.12)] text-[hsl(var(--hi-status-success))]">Pagato</Badge>
              <Button size="sm" variant="outline" onClick={() => downloadPdf(p)} data-testid={`pay-pdf-${p.id}`}><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
            </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!pay} onOpenChange={(v) => !v && setPay(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-xl">Registra pagamento · {pay?.operator_name}</DialogTitle></DialogHeader>
          {pay && <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Base ({pay.hours}h)</span><span>{eur(pay.base_hourly)}</span></div>
            {pay.fixed > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Fisso</span><span>{eur(pay.fixed)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Commissioni servizi</span><span>{eur(pay.commission_service)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Commissioni prodotti</span><span>{eur(pay.commission_product)}</span></div>
            <div className="flex justify-between font-semibold border-t border-border pt-2"><span>Totale calcolato</span><span>{eur(pay.total_gross)}</span></div>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <div className="space-y-1.5"><Label className="text-xs">Rettifica (±)</Label><Input type="number" value={adjustment} onChange={(e) => setAdjustment(e.target.value)} data-testid="pay-adjustment" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Bonus (€)</Label><Input type="number" value={bonus} onChange={(e) => setBonus(e.target.value)} data-testid="pay-bonus" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Trattenute</Label><Input type="number" value={deductions} onChange={(e) => setDeductions(e.target.value)} data-testid="pay-deductions" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Anticipi</Label><Input type="number" value={advances} onChange={(e) => setAdvances(e.target.value)} data-testid="pay-advances" /></div>
            </div>
            <div className="space-y-1.5"><Label>Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opzionale" data-testid="pay-note" /></div>
            <div className="flex justify-between font-semibold text-[hsl(var(--hi-gold))] pt-1 border-t border-border mt-1"><span>Netto da pagare</span><span data-testid="pay-net">{eur(netToPay)}</span></div>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> Alla conferma verrà generato il cedolino PDF e inviato via email al collaboratore.</p>
          </div>}
          <DialogFooter><Button onClick={confirmPay} disabled={paying} data-testid="pay-confirm">{paying ? "Invio in corso…" : "Conferma pagamento"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configurazione compenso */}
      <Dialog open={!!cfg} onOpenChange={(v) => !v && setCfg(null)}>
        <DialogContent data-testid="comp-dialog" className="max-h-[90vh] overflow-y-auto hi-scroll">
          <DialogHeader><DialogTitle className="font-heading text-xl">Compenso · {cfg?.operator_name}</DialogTitle></DialogHeader>
          {cfg && <div className="space-y-3">
            <p className="text-[11px] text-muted-foreground -mt-1">I componenti sono cumulabili: fisso + oraria + commissioni + bonus si sommano.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Modello</Label>
                <Select value={cfg.type} onValueChange={(v) => setCfg({ ...cfg, type: v })}>
                  <SelectTrigger data-testid="comp-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMP_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Frequenza</Label>
                <Select value={cfg.frequency} onValueChange={(v) => setCfg({ ...cfg, frequency: v })}>
                  <SelectTrigger data-testid="comp-freq"><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQ.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Paga oraria (€)</Label><Input type="number" value={cfg.hourly_rate} onChange={(e) => setCfg({ ...cfg, hourly_rate: e.target.value })} data-testid="comp-hourly" /></div>
              <div className="space-y-1.5"><Label>Fisso periodo (€)</Label><Input type="number" value={cfg.fixed_salary} onChange={(e) => setCfg({ ...cfg, fixed_salary: e.target.value })} data-testid="comp-fixed" /></div>
              <div className="space-y-1.5"><Label>% Servizi</Label><Input type="number" value={cfg.service_pct} onChange={(e) => setCfg({ ...cfg, service_pct: e.target.value })} data-testid="comp-service-pct" /></div>
              <div className="space-y-1.5"><Label>% Prodotti</Label><Input type="number" value={cfg.product_pct} onChange={(e) => setCfg({ ...cfg, product_pct: e.target.value })} data-testid="comp-product-pct" /></div>
            </div>
            <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> Obiettivo minimo fatturato (€)</Label><Input type="number" value={cfg.revenue_target} onChange={(e) => setCfg({ ...cfg, revenue_target: e.target.value })} data-testid="comp-target" /><p className="text-[11px] text-muted-foreground">La % Servizi si applica solo sulla parte oltre l'obiettivo (ignorato se usi le fasce).</p></div>

            {/* Fasce progressive: la % si applica solo alla parte oltre ogni soglia */}
            <div className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> Fasce progressive servizi</Label>
                <Button type="button" size="sm" variant="outline" data-testid="comp-tier-add"
                  onClick={() => setCfg({ ...cfg, tiers: [...(cfg.tiers || []), { over: "", pct: "" }] })}>+ Fascia</Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Ogni percentuale si applica SOLO alla parte di fatturato oltre la soglia. Es. 10% oltre 4.500 €, 15% oltre 6.000 €.</p>
              {(cfg.tiers || []).length === 0 && <p className="text-[11px] text-muted-foreground italic">Nessuna fascia: viene usata la % Servizi piatta.</p>}
              {(cfg.tiers || []).map((t, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`comp-tier-${i}`}>
                  <span className="text-xs text-muted-foreground">oltre</span>
                  <Input type="number" className="w-28" placeholder="€" value={t.over}
                    onChange={(e) => { const ts = [...cfg.tiers]; ts[i] = { ...ts[i], over: e.target.value }; setCfg({ ...cfg, tiers: ts }); }} data-testid={`comp-tier-over-${i}`} />
                  <span className="text-xs text-muted-foreground">€ →</span>
                  <Input type="number" className="w-20" placeholder="%" value={t.pct}
                    onChange={(e) => { const ts = [...cfg.tiers]; ts[i] = { ...ts[i], pct: e.target.value }; setCfg({ ...cfg, tiers: ts }); }} data-testid={`comp-tier-pct-${i}`} />
                  <span className="text-xs text-muted-foreground">%</span>
                  <Button type="button" size="icon" variant="ghost" className="h-8 w-8" data-testid={`comp-tier-del-${i}`}
                    onClick={() => setCfg({ ...cfg, tiers: cfg.tiers.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>

            {/* Bonus / correzione manuale */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Bonus / premio (€)</Label><Input type="number" value={cfg.bonus} onChange={(e) => setCfg({ ...cfg, bonus: e.target.value })} data-testid="comp-bonus" /></div>
              <div className="space-y-1.5"><Label>Nota bonus (facoltativa)</Label><Input value={cfg.bonus_note} onChange={(e) => setCfg({ ...cfg, bonus_note: e.target.value })} data-testid="comp-bonus-note" /></div>
            </div>

            {/* Servizi esclusi dalle commissioni */}
            <div className="space-y-1.5">
              <Label>Servizi esclusi dalle commissioni</Label>
              <p className="text-[11px] text-muted-foreground">Es. escludi la piega. I servizi selezionati non entrano nella base delle commissioni servizi.</p>
              <div className="max-h-36 overflow-y-auto hi-scroll rounded-lg border border-border p-2 space-y-1" data-testid="comp-excluded-list">
                {services.map((s) => {
                  const on = (cfg.excluded_service_ids || []).includes(s.id);
                  return (
                    <button type="button" key={s.id} data-testid={`comp-excl-${s.id}`}
                      onClick={() => setCfg({ ...cfg, excluded_service_ids: on ? cfg.excluded_service_ids.filter((x) => x !== s.id) : [...(cfg.excluded_service_ids || []), s.id] })}
                      className={`w-full text-left text-sm rounded px-2 py-1 flex justify-between items-center ${on ? "bg-[hsl(var(--hi-gold-soft))] font-medium" : "hover:bg-accent"}`}>
                      <span className="truncate">{s.name}</span>
                      {on && <span className="text-[10px] text-[hsl(var(--hi-gold))]">escluso</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCfg(null)}>Annulla</Button>
            <Button onClick={saveCfg} disabled={savingCfg} data-testid="comp-save">Salva compenso</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Riepilogo lavori del collaboratore nel periodo selezionato */}
      <Dialog open={detailOpen} onOpenChange={(v) => { setDetailOpen(v); if (!v) setDetail(null); }}>
        <DialogContent data-testid="detail-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto hi-scroll">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-[hsl(var(--hi-gold))]" /> Riepilogo lavori · {detail?.operator_name || ""}
            </DialogTitle>
            <p className="text-xs text-muted-foreground">Periodo {fmtDate(from)} → {fmtDate(to)}</p>
          </DialogHeader>

          {detailLoading && (
            <div className="space-y-2 py-2" data-testid="detail-loading">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          )}

          {!detailLoading && detail && (
            <div className="space-y-5">
              {/* Servizi svolti */}
              <div>
                <div className="flex items-center gap-1.5 mb-2 font-medium text-sm"><Scissors className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Servizi svolti</div>
                {detail.service_lines.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic px-1" data-testid="detail-services-empty">Nessun servizio nel periodo.</p>
                ) : (
                  <div className="rounded-lg border border-border overflow-hidden">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Servizio</TableHead>
                        <TableHead className="text-right">Prezzo</TableHead><TableHead className="text-right">%</TableHead>
                        <TableHead className="text-right">Guadagno</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {detail.service_lines.map((l, i) => (
                          <TableRow key={i} data-testid={`detail-service-row-${i}`}>
                            <TableCell className="text-xs whitespace-nowrap">{fmtDate(l.date)}</TableCell>
                            <TableCell className="text-sm">{l.client_name}</TableCell>
                            <TableCell className="text-sm">
                              {l.name}
                              {l.excluded && <Badge variant="secondary" className="ml-1.5 text-[9px] px-1.5 py-0">escluso</Badge>}
                            </TableCell>
                            <TableCell className="text-right tabular text-sm">{eur(l.net)}</TableCell>
                            <TableCell className="text-right tabular text-xs text-muted-foreground">{l.excluded ? "—" : `${l.pct}%`}</TableCell>
                            <TableCell className="text-right tabular text-sm font-medium">{eur(l.earning)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {detail.service_pct_is_effective && detail.service_lines.some((l) => !l.excluded) && (
                  <p className="text-[11px] text-muted-foreground mt-1.5">La percentuale mostrata è quella <strong>effettiva</strong> (commissione a fasce progressive / oltre obiettivo), ripartita proporzionalmente sul netto di ogni servizio.</p>
                )}
              </div>

              {/* Prodotti venduti */}
              {detail.product_lines.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2 font-medium text-sm"><Package className="h-4 w-4 text-[hsl(var(--hi-gold))]" /> Prodotti venduti</div>
                  <div className="rounded-lg border border-border overflow-hidden">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Prodotto</TableHead>
                        <TableHead className="text-right">Prezzo</TableHead><TableHead className="text-right">%</TableHead>
                        <TableHead className="text-right">Commissione</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {detail.product_lines.map((l, i) => (
                          <TableRow key={i} data-testid={`detail-product-row-${i}`}>
                            <TableCell className="text-xs whitespace-nowrap">{fmtDate(l.date)}</TableCell>
                            <TableCell className="text-sm">{l.client_name}</TableCell>
                            <TableCell className="text-sm">{l.name}</TableCell>
                            <TableCell className="text-right tabular text-sm">{eur(l.net)}</TableCell>
                            <TableCell className="text-right tabular text-xs text-muted-foreground">{l.pct}%</TableCell>
                            <TableCell className="text-right tabular text-sm font-medium">{eur(l.earning)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Riepilogo economico del periodo — combacia con la riga Retribuzioni */}
              <div className="rounded-lg border border-border bg-accent/40 p-4 space-y-1.5 text-sm" data-testid="detail-summary">
                <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Ore lavorate</span><span className="tabular font-medium">{detail.summary.hours}h</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Compenso base (oraria + fisso)</span><span className="tabular font-medium" data-testid="detail-base">{eur(detail.summary.base)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Totale commissioni servizi</span><span className="tabular font-medium" data-testid="detail-comm-service">{eur(detail.summary.commission_service)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Totale commissioni prodotti</span><span className="tabular font-medium" data-testid="detail-comm-product">{eur(detail.summary.commission_product)}</span></div>
                {detail.summary.bonus > 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Bonus / obiettivi{detail.summary.bonus_note ? ` · ${detail.summary.bonus_note}` : ""}</span><span className="tabular font-medium" data-testid="detail-bonus">{eur(detail.summary.bonus)}</span></div>
                )}
                {detail.summary.revenue_target > 0 && (
                  <div className="flex justify-between text-xs"><span className="text-muted-foreground">Fatturato / obiettivo</span><span className="tabular">{eur(detail.summary.revenue_total)} / {eur(detail.summary.revenue_target)} ({detail.summary.target_progress}%)</span></div>
                )}
                <div className="flex justify-between border-t border-border pt-2 mt-1 font-semibold text-[hsl(var(--hi-gold))]"><span>Totale lordo del periodo</span><span className="tabular text-base" data-testid="detail-total-gross">{eur(detail.summary.total_gross)}</span></div>
              </div>
              <p className="text-[11px] text-muted-foreground">I totali coincidono con quelli mostrati nella riga Retribuzioni del periodo selezionato.</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetailOpen(false); setDetail(null); }} data-testid="detail-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
